/**
 * data-export.ts
 * Copyright (c) 2018, My Coverage Plan, Inc.
 * All rights reserved.
 */

import { AbstractControl, FormArray, FormControl, Validators }
    from "@angular/forms";
import { SafeUrl } from "@angular/platform-browser";

import { NumberTypes, ServerDataType } from "../common/data/data-types";
import { TypedDataHolder, TypedData } from "../common/data/typed-data";
import { SocketFile } from "../server/connection/download-socket";
import { AggregationFilterNode }
    from "../server/messages/aggregation/start-aggregator-request";
import {
    CancelDataExportRequest,
    CreateDataExportResponse,
    DataExportReportResponse,
    DataExportStatusRequest,
    DataExportStatusResponse,
    PauseDataExportRequest,
    ResumeDataExportRequest,
    TableDescription
} from "../server/messages/export/";
import { ColumnDescription }
    from "../server/messages/export/start-data-export-request";
import { ReplyVerifyingMessage }
    from "../server/messages/general/reply-message";
import { oneline } from "../support/utilities/functions";
import { Exporter } from "./exporter";
import { ConstantNode, FilterNode, OperatorNode, UndefinedNode, VariableNode }
    from "./filter-node";
import { Equals, Is, Operators } from "./operators";
import { DataExportSchema, TypedColumnDescription } from "./schemas";
import { SummarizationByName } from "./summarization";
import { ActivityType, BasicActivityCommand, ToolboxActivity }
   from "./toolbox-activity";
import {
    AggVariable,
    ContextResult,
    ContextResultSummary,
    FrequencyData,
    FrequencyResult,
    StdVariable,
    Variable
} from "./typed-parameter";

/**
 * The values needed to successfully initiate a download.
 */
interface DownloadCredentials
{
    token : string;
    secret : string;
}

/**
 * A {@link DataExport} is a server process where records are combined into a
 * single exported file. Unlike the {@link Aggregation} tools, the final result
 * is a binary file, to be handed over to the user. As such, exports do not
 * contain as much UI for displaying reports, but *do* contain additional logic
 * for handling downloads.
 *
 * A single data export instance represents one task for a particular static
 * {@link Exporter}. As with each {@link ToolboxActivity}, these tasks will
 * correspond with server *Agents*.
 */
export class DataExport extends ToolboxActivity
{
    constructor (
        public id : number,
        public filterOptions : AggVariable[] = [],
        public columnOptions : StdVariable[] = [],
        public tableOptions : TableDescription[] = [],
        public parent : Exporter
    )
    {
        super();
        this.title =
            `${this.parent.displayName} ${++this.parent.childCounter}`;

        tableOptions.forEach((table : TableDescription) => {
            table.members.forEach((varName : string) => {
                const match : StdVariable = this.columnOptions.find(
                    (target : StdVariable) => target.name === varName);
                (match as any).table = table.table;
                (match as any).membershipClass = "text-info";
            });
        });

        this.columnOptions.forEach((variable : StdVariable & any) => {
            if (variable.cardinality !== 1 && !variable.table)
            {
                let tableName : string;
                if (tableOptions.some(
                    (table : TableDescription) => table.members.some(
                        (member : string) => {
                            if (member === variable.name)
                            {
                                tableName = table.table;
                                return true;
                            }
                            return false;
                        })))
                {
                    variable.table = tableName;
                }
                variable.membershipClass = "text-info";
            }
        });
    }

    public readSchema (schema : DataExportSchema) : void
    {
        this.columns = schema.columns;
        this.columnSelection = schema.columns.map(
            (tcd : TypedColumnDescription) => {
              const out = Object.assign(
                {},
                tcd.baseType as any,
                {summarization: SummarizationByName.get(tcd.summarization)},
                {name: tcd.name},
                {displayName: tcd.columnName});
              if (tcd.filter)
              {
                out.filter = OperatorNode.simpleFromSchema(
                  tcd.filter as AggregationFilterNode,
                  this.filterOptions as any[],
                  true,
                  out);
              }
              return out;
            });
        this.headersControl.setValue(schema.useHeaders);

        // Note this is a little idiosyncratic, but the custom delimiter for
        // this task is stored on the schema's format (as if it were a property
        // of that format itself, rather than the specific task instance).
        this.delimiterControl.setValue(schema.format.collectionDelimiter);

        if (schema.filter.nodeType === "operator")
        {
            this.filter = OperatorNode.fromSchema(
                schema.filter as AggregationFilterNode,
                this.filterOptions as any[],
                true,
                null,
                true);
        }
        else
        {
            // Either there's no filter or the filter isn't rooted by an
            // operator, which is an illegal configuration.
        }

        if (schema.exportFilename !== "")
        {
          this.filenameControl.setValue(schema.exportFilename);
        }

        if (Number.isSafeInteger(schema.childCounter))
        {
            schema.childCounter++;
        }
        else
        {
            schema.childCounter = 1;
        }
        this.title = schema.schemaName + " " + schema.childCounter;
    }

    public copyFrom (other : DataExport) : void
    {
        this.columns = other.columns.map(
            (x : ColumnDescription) => Object.assign({}, x));
        this.columnSelection = other.columnSelection.map(
            (cs : Variable<ServerDataType>) => {
                const out : any = Object.assign({}, cs);
                if ("summarization" in out)
                {
                    out.summarization =
                        Object.assign({}, (cs as any).summarization);
                }
                return out;
            });
        this.headersControl.setValue(other.useHeaders);
        this.delimiterControl.setValue(other.delimiterControl.value);

        this.filter = OperatorNode.fromSchema(
            other.filter.marshal(),
            this.filterOptions as any[],
            true,
            null,
            true);
        this.filenameControl.setValue(other.filenameControl.value);
    }

    public columnSelection : StdVariable[] = [];
    public columns : ColumnDescription[] = [];
    public groupId? : number;

    public downloadCredentials : DownloadCredentials;
    public downloadFile : SocketFile;
    public downloadURL : string;
    public downloadSafeURL : SafeUrl;
    public checksum : number;
    public checksumHex : string;
    public expectedFilesize : number;
    public customDelimiter : string = this.parent.collectionDelimiter || ",";
    public useHeaders : boolean = true;
    public headersControl : FormControl = new FormControl(true);
    public excelDatesControl : FormControl = new FormControl(true);
    public dropCollFilterEmptiesControl : FormControl = new FormControl(true);

    public delimiterControl : FormControl = new FormControl(
            {
                value: this.customDelimiter,
                disabled: false // !this.parent || !this.parent.allowsInlining
            },
            Validators.required);
    public membershipControl : FormArray = new FormArray([]);
    public bomControl : FormControl = new FormControl(false);
    public labelControl : FormControl = new FormControl(
        {value: "", disabled: true});

    public configurationControls : AbstractControl[] = [
        this.membershipControl,
        this.delimiterControl,
        this.headersControl,
        this.excelDatesControl,
        this.dropCollFilterEmptiesControl,
        this.labelControl
    ];

    public filenameControl : FormControl = new FormControl(
        this.parent.defaultFilename());
    public getDownloadName () : string
    {
      const given : string = this.filenameControl.value as string;
      if (!given)
      {
        return "untitled";
      }
      return given.replace("[DATE]", TypedData.dateToInput(new Date()));
    }

    public static fromOptions (
        rsp : CreateDataExportResponse,
        parent : Exporter
    ) : DataExport
    {
        return new DataExport(
            rsp.exportId,
            rsp.filterOptions,
            rsp.targetVariableOptions,
            rsp.tables,
            parent
        );
    }

    public clearResults () : void
    {
        this.downloadCredentials = null;
        this.downloadFile = null;
        this.downloadURL = null;
        this.downloadSafeURL = null;
        this.checksum = null;
        this.checksumHex = null;
        this.expectedFilesize = null;
    }

    public readMessageReport (derr : DataExportReportResponse) : void
    {
        this.readCompletion(derr);
        this.checksum = derr.checksumUnsigned;
        this.checksumHex = "0x" + derr.checksumUnsigned.toString(16);
        this.expectedFilesize = derr.bytesSent;
    }

    public requestFor (action : BasicActivityCommand) : ReplyVerifyingMessage
    {
        switch (action)
        {
            case "cancel":
                return new CancelDataExportRequest(this.id);
            case "pause":
                return new PauseDataExportRequest(this.id);
            case "resume":
                return new ResumeDataExportRequest(this.id);
            case "status":
                return new DataExportStatusRequest(this.id);
            default:
                throw new Error(`Unsupported activity request: ${action}`);
        }
    }
}



// WEBPACK FOOTER //
// ./~/angular-router-loader/src!./app/aggregation/data-export.ts